import {
  GridColDef,
  GridPaginationModel,
  GridSortModel,
  GridValidRowModel,
  GridColumnVisibilityModel,
} from "@mui/x-data-grid-premium";
import { CustomGridColDef } from "components/DataGrid";
import { useGridPersistence } from "hooks/omnisearch-v2";
import { omit } from "lodash-es";
import { useCallback, useMemo } from "react";
import { DateShortcutType } from "util/dateRangeShortcuts";

import { OmnisearchPersistenceScope, useOmnisearchInput } from "./useOmnisearchInput";
import { usePage } from "./usePage";

export type FilterKey = {
  key: string;
  header: string;
  filterKeyType?: FilterKeyType;
  HelpTooltip?: React.ComponentType;
  enumValues?: string[];
  enumPresentationFunction?: (enumValue: string) => string;
  /**
   * Forwarded to the default enum pretty-printer when no
   * `enumPresentationFunction` is provided. When `true`, two-letter US state
   * and Canadian province codes are **not** preserved as uppercase.
   *
   * Set this on non-jurisdictional enums whose values happen to contain a
   * token that collides with a state code. The default pretty-printer treats
   * `IN` (Indiana), `OR` (Oregon), `ME` (Maine), etc. as state codes and
   * keeps them uppercase, so:
   *
   *   - `IN_PROGRESS` → "IN Progress"  (default)
   *   - `IN_PROGRESS` → "In Progress"  (with this flag set)
   *
   * Leave unset for enums that genuinely carry jurisdiction codes
   * (`TX_ANNUAL_WASTE_SUMMARY`, `ME_ANNUAL_REPORT`, etc.), where the
   * uppercase rendering is correct.
   */
  ignoreStateAbbreviations?: boolean;
  /** @deprecated Chips default on (HISTORICAL). Set `false` to hide them. */
  dateRangeShortcuts?: boolean;
  /** Which shortcut preset the date-range filter shows. Defaults to HISTORICAL. */
  dateShortcutType?: DateShortcutType;
  /**
   * Earliest selectable date for a `date`-range filter, as an ISO date (YYYY-MM-DD).
   * Disables earlier days in the picker — e.g. a rolling-window filter that must not
   * reach past its floor.
   */
  minDate?: string;
  disableFilterWhen?: {
    field: string;
    value: string | boolean;
  };
  nullCheckboxLabel?: string;
};

export type FilterKeyType =
  | "boolean"
  | "enum"
  | "enumAutocomplete"
  | "facility"
  | "wasteFacility"
  // Tenant-scoped searchable multi-select of vendors present in the tenant's
  // waste data (ENG-15174). Backed by the `tenantWasteVendors` query.
  | "wasteVendor"
  | "multipleFacility"
  | "wasteMultipleFacility"
  | "multipleTenant"
  | "multipleUser"
  // Tenant-scoped user picker restricted to customer users.
  | "multipleCustomerUser"
  // Same UX as `multipleUser`, but the underlying picker queries
  // `staffSearchAnyUser` so it can return users across every tenant. Use
  // on staff tooling that doesn't have a current-tenant context.
  | "multipleStaffAnyUser"
  | "number"
  | "stateOfMatter"
  | "tenant"
  | "text"
  | "time"
  | "date"
  // Tag types - must match TagFilterType in FilterPopover.tsx
  | "facilityTags"
  | "taskTags"
  | "workflowTags"
  | "wasteTags"
  | "documentTags";

export type OmnisearchGridColDef<Row extends GridValidRowModel = Record<string, any>> =
  CustomGridColDef<Row> & {
    filterKeyType?: FilterKeyType;
    enumValues?: string[];
    enumPresentationFunction?: (enumValue: string) => string;
    /**
     * Forwarded to the default enum pretty-printer when no
     * `enumPresentationFunction` is set. When `true`, two-letter US state
     * and Canadian province codes are **not** preserved as uppercase, so
     * (for example) `IN_PROGRESS` renders as "In Progress" instead of
     * "IN Progress". See `FilterKey.ignoreStateAbbreviations` for the full
     * write-up.
     */
    ignoreStateAbbreviations?: boolean;
    HelpTooltip?: React.ComponentType;
    /** @deprecated Date-range chips now default on (HISTORICAL). Set `false` to hide them. */
    dateRangeShortcuts?: boolean;
    /** Which shortcut preset set the date-range filter shows. Defaults to HISTORICAL. */
    dateShortcutType?: DateShortcutType;
    disableFilterWhen?: {
      field: string;
      value: string | boolean;
    };
    /** Render an "is null" checkbox under the text input (e.g. "Unassigned only"). */
    nullCheckboxLabel?: string;
  };

// Stable empty arrays to prevent useMemo recomputation from default prop references
const EMPTY_ADDITIONAL_FILTERS: FilterKey[] = [];
const EMPTY_EXCLUDE_FILTERS: string[] = [];

interface UseOmnisearchDatagridProps<Row extends GridValidRowModel> {
  persistenceKey: string;
  isURLDriven?: boolean;
  initialSortModel?: GridSortModel;
  initialPageSize?: number;
  initialColumnVisibilityModel?: GridColumnVisibilityModel;
  isMobile?: boolean;
  columns?: OmnisearchGridColDef<Row>[];
  initialFilters?: { field: string; value: string | boolean }[];
  additionalFilters?: FilterKey[];
  excludeFilters?: string[];
  /** Debounce delay in ms for typing. Defaults to 500ms. */
  debounceMs?: number;
  /** When true, the omnisearch filter string is persisted to localStorage and
   *  restored on fresh page loads that have no URL omnisearch param. */
  persistOmnisearch?: boolean;
  /** Scope for the persisted omnisearch value. "tenant" (default) keys storage
   *  by the current tenant and disables persistence when none is resolvable;
   *  use "global" for grids without tenant context (staff-wide dashboards). */
  persistOmnisearchScope?: OmnisearchPersistenceScope;
  /** When false, the sort model resets to `initialSortModel` on each mount
   *  instead of being persisted. Defaults to true. */
  persistSortModel?: boolean;
}

/**
 * @deprecated Use `OmnisearchDataGridV2` from `hooks/omnisearch-v2` instead.
 */
export function useOmnisearchDatagrid<Row extends GridValidRowModel = Record<string, any>>({
  persistenceKey,
  isURLDriven = true,
  initialSortModel = [],
  initialPageSize = 25,
  initialColumnVisibilityModel,
  isMobile,
  columns: columnsInput = [],
  initialFilters = [],
  additionalFilters = EMPTY_ADDITIONAL_FILTERS,
  excludeFilters = EMPTY_EXCLUDE_FILTERS,
  debounceMs,
  persistOmnisearch,
  persistOmnisearchScope,
  persistSortModel,
}: UseOmnisearchDatagridProps<Row>) {
  const {
    omnisearch,
    debouncedOmnisearch,
    getOmnisearchValue,
    createUrlFromOmnisearchKeyValues,
    setOmnisearch,
    setOmnisearchImmediate,
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
  } = useOmnisearchInput({
    isURLDriven,
    initialFilters,
    debounceMs,
    persistenceKey: persistOmnisearch ? persistenceKey : undefined,
    persistenceScope: persistOmnisearchScope,
  });

  const { page, setPage } = usePage({ isURLDriven });

  const {
    pageSize,
    sortModel,
    columnVisibilityModel,
    columnWidthModel,
    setPageSize,
    setSortModel,
    resetSortModel,
    setColumnVisibilityModel,
    setColumnWidthModel,
    unsetColumnWidthModel,
    resetColumnSettings,
  } = useGridPersistence({
    persistenceKey,
    initialSortModel,
    initialPageSize,
    initialColumnVisibilityModel,
    isMobile,
    persistSortModel,
  });

  const paginationModel: GridPaginationModel = useMemo(
    () => ({ page, pageSize }),
    [page, pageSize]
  );

  const setPaginationModel = useCallback(
    (model: GridPaginationModel) => {
      setPage(model.page);
      setPageSize(model.pageSize);
    },
    [setPage, setPageSize]
  );

  const columns = useMemo(
    () =>
      columnsInput.map<GridColDef<Row>>(
        (col) =>
          ({
            ...omit(col, [
              "omnisearchKey",
              "omnisearchFilterKeyType",
              "omnisearchEnumValues",
              "omnisearchHelpTooltip",
              "width",
              "flex",
            ]),
            width: columnWidthModel[col.field] ?? col.width,
            flex: columnWidthModel[col.field] ? undefined : col.flex,
          }) as GridColDef<Row>
      ),
    [columnsInput, columnWidthModel]
  );

  const columnFilterKeys = useMemo(() => {
    return columnsInput
      .filter(
        (col) =>
          !excludeFilters.includes(col.field) && col.filterable !== false && col.type !== "actions"
      )
      .reduce<FilterKey[]>((keys, col) => {
        keys.push({
          header: col.headerName ?? col.field,
          key: col.field,
          filterKeyType: col.filterKeyType,
          enumValues: col.enumValues,
          HelpTooltip: col.HelpTooltip,
          enumPresentationFunction: col.enumPresentationFunction,
          ignoreStateAbbreviations: col.ignoreStateAbbreviations,
          dateRangeShortcuts: col.dateRangeShortcuts,
          dateShortcutType: col.dateShortcutType,
          disableFilterWhen: col.disableFilterWhen,
          nullCheckboxLabel: col.nullCheckboxLabel,
        });
        return keys;
      }, [])
      .concat(additionalFilters);
  }, [columnsInput, excludeFilters, additionalFilters]);

  return {
    omnisearch,
    debouncedOmnisearch,
    setOmnisearch,
    setOmnisearchImmediate,
    getOmnisearchValue,
    createUrlFromOmnisearchKeyValues,
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
    page,
    pageSize,
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    resetSortModel,
    columns,
    columnVisibilityModel,
    columnWidthModel,
    setColumnVisibilityModel,
    setColumnWidthModel,
    unsetColumnWidthModel,
    resetColumnSettings,
    columnFilterKeys,
  };
}
